import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { DeleteModal } from '../../../components';
import { constructionReviewsService, ConstructionReview } from '../../../services/construction-reviews.service';
import { CR_ATTRIBUTE_ORDER } from '../../../constants/app.constants';

interface EditableEntry {
  key: string;
  value: string;
  isUserModified: boolean;
  isUserAdded: boolean;
}

interface CREditViewProps {
  cr: ConstructionReview;
  onUpdate: (updatedCR: ConstructionReview) => void;
  onDelete: () => void;
}

export const CREditView: React.FC<CREditViewProps> = ({ cr, onUpdate, onDelete }) => {
  const [editableEntries, setEditableEntries] = useState<EditableEntry[]>([]);
  const [newEntryKey, setNewEntryKey] = useState('');
  const [newEntryValue, setNewEntryValue] = useState('');
  const [isAddingEntry, setIsAddingEntry] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Initialize editable entries from the CR data
    const entries: EditableEntry[] = [];
    const processedKeys = new Set<string>();
    
    // First, add entries in the predefined order
    CR_ATTRIBUTE_ORDER.forEach(key => {
      if (cr.updated_cr.hasOwnProperty(key)) {
        const isUserModified = cr.changes.hasOwnProperty(key);
        entries.push({
          key,
          value: String(cr.updated_cr[key]),
          isUserModified,
          isUserAdded: false
        });
        processedKeys.add(key);
      }
    });
    
    // Then, add any remaining entries that weren't in the predefined order
    // This includes user-added custom attributes
    Object.entries(cr.updated_cr).forEach(([key, value]) => {
      if (!processedKeys.has(key)) {
        const isUserModified = cr.changes.hasOwnProperty(key);
        entries.push({
          key,
          value: String(value),
          isUserModified,
          isUserAdded: !cr.generated_cr.hasOwnProperty(key)
        });
      }
    });

    setEditableEntries(entries);
  }, [cr]);

  const handleValueChange = (key: string, newValue: string) => {
    setEditableEntries(prevEntries =>
      prevEntries.map(entry => {
        if (entry.key === key) {
          const originalValue = String(cr.generated_cr[key] ?? '');
          const isModified = newValue !== originalValue;
          return { ...entry, value: newValue, isUserModified: isModified };
        }
        return entry;
      })
    );
  };

  const handleAddEntry = () => {
    setIsAddingEntry(true);
    setNewEntryKey('');
    setNewEntryValue('');
  };

  const handleSaveNewEntry = () => {
    if (newEntryKey.trim()) {
      setEditableEntries(prev => [...prev, {
        key: newEntryKey.trim(),
        value: newEntryValue,
        isUserModified: true,
        isUserAdded: true
      }]);
      setIsAddingEntry(false);
      setNewEntryKey('');
      setNewEntryValue('');
    }
  };

  const handleCancelNewEntry = () => {
    setIsAddingEntry(false);
    setNewEntryKey('');
    setNewEntryValue('');
  };

  const handleRemoveEntry = (key: string) => {
    setEditableEntries(prev => prev.filter(entry => entry.key !== key));
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Build updated CR data
      const updatedCR: Record<string, any> = {};
      editableEntries.forEach(entry => {
        updatedCR[entry.key] = entry.value;
      });

      // Calculate changes from original generated_cr
      const changes: Record<string, any> = {};
      editableEntries.forEach(entry => {
        if (entry.isUserModified || entry.isUserAdded) {
          if (!(entry.key in cr.generated_cr)) {
            // User added this entry
            changes[entry.key] = {
              from: null,
              to: entry.value
            };
          } else if (String(cr.generated_cr[entry.key]) !== String(entry.value)) {
            // User modified this entry
            changes[entry.key] = {
              from: cr.generated_cr[entry.key],
              to: entry.value
            };
          }
        }
      });

      const result = await constructionReviewsService.update(cr.id, {
        updated_cr: updatedCR,
        changes: changes
      });

      onUpdate(result);
      toast.success('Construction Review updated successfully!');
    } catch (error) {
      console.error('Failed to update CR:', error);
      toast.error('Failed to update Construction Review');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await constructionReviewsService.delete(cr.id);
      onDelete();
      toast.success('Construction Review deleted successfully!');
    } catch (error) {
      console.error('Failed to delete CR:', error);
      toast.error('Failed to delete Construction Review');
    }
  };

  const handleCopy = async () => {
    // Apply CR_ATTRIBUTE_ORDER for consistent ordering
    const entryMap = new Map(editableEntries.map(entry => [entry.key, entry]));
    const orderedEntries: EditableEntry[] = [];
    const processedKeys = new Set<string>();
    
    // First, add entries in the predefined order
    CR_ATTRIBUTE_ORDER.forEach(key => {
      if (entryMap.has(key)) {
        orderedEntries.push(entryMap.get(key)!);
        processedKeys.add(key);
      }
    });
    
    // Then, add any remaining entries that weren't in the predefined order
    editableEntries.forEach(entry => {
      if (!processedKeys.has(entry.key)) {
        orderedEntries.push(entry);
      }
    });

    const text = orderedEntries
      .map(entry => `${entry.key}: ${entry.value}`)
      .join('\n');

    try {
      await navigator.clipboard.writeText(text);
      toast.success('Construction Review copied to clipboard!', {
        duration: 2000,
        icon: '📋'
      });
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const modifiedCount = Object.keys(cr.changes).length;
  const totalEntries = editableEntries.length;

  return (
    <div className="space-y-4">
      {/* Actions & Stats Bar */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden border-l-4 border-l-blue-500">
        <div className="px-5 py-3">
          <div className="flex items-center gap-3">
            {/* Stats */}
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-[9px] xl:text-[10px] 3xl:text-xs text-slate-500">
                <span className="font-bold text-slate-600 mr-1">Created</span>
                {new Date(cr.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                <span className="text-slate-400 ml-1">{new Date(cr.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-[9px] xl:text-[10px] 3xl:text-xs text-slate-500">
                <span className="font-bold text-slate-600 mr-1">Updated</span>
                {new Date(cr.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                <span className="text-slate-400 ml-1">{new Date(cr.updated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-[9px] xl:text-[10px] 3xl:text-xs text-blue-600 font-semibold">
                {totalEntries} fields
              </span>
              {modifiedCount > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-[9px] xl:text-[10px] 3xl:text-xs text-amber-600 font-semibold">
                  {modifiedCount} modified
                </span>
              )}
            </div>

            <div className="flex-1" />

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] xl:text-xs 3xl:text-sm font-medium text-slate-600 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy to Clipboard
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[10px] xl:text-xs 3xl:text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm shadow-emerald-600/20 transition-colors disabled:bg-gray-400 disabled:shadow-none"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                )}
                Save Changes
              </button>
            </div>

            {/* Delete — separated from main actions */}
            <div className="w-px h-6 bg-slate-200" />
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] xl:text-xs 3xl:text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg shadow-sm shadow-red-500/20 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Editable Entries */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden border-l-4 border-l-slate-300">
        {/* Section Header */}
        <div className="px-4 py-2 bg-slate-50/80 border-b border-slate-200 flex items-center">
          <div className="flex items-center gap-2">
            <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
            </svg>
            <span className="text-[10px] xl:text-xs 3xl:text-sm font-semibold text-slate-600 uppercase tracking-wider">CR Attributes</span>
          </div>
        </div>

        {/* Entries List */}
        <div className="max-h-[65vh] overflow-auto divide-y divide-slate-100">
          {editableEntries.map((entry, index) => (
            <div
              key={`${entry.key}-${index}`}
              className={`group flex items-center gap-2.5 px-3 py-1.5 hover:bg-slate-50/50 transition-colors ${
                entry.isUserAdded ? 'bg-emerald-50/30' : entry.isUserModified ? 'bg-amber-50/20' : ''
              }`}
            >
              <div className={`w-0.5 self-stretch rounded-full shrink-0 ${
                entry.isUserAdded ? 'bg-emerald-400' : entry.isUserModified ? 'bg-amber-400' : 'bg-blue-200'
              }`} />
              <div className="flex items-center gap-1.5 w-[26%] shrink-0">
                <span className="text-[8px] xl:text-[10px] 3xl:text-xs font-semibold text-blue-600 uppercase tracking-wider break-all">
                  {entry.key}
                </span>
                {entry.isUserAdded ? (
                  <>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[7px] xl:text-[8px] 3xl:text-[10px] font-bold uppercase tracking-wide shrink-0">
                      New
                    </span>
                    <button
                      onClick={() => handleRemoveEntry(entry.key)}
                      className="opacity-0 group-hover:opacity-100 p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-all shrink-0"
                      title="Remove entry"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </>
                ) : entry.isUserModified ? (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[7px] xl:text-[8px] 3xl:text-[10px] font-bold uppercase tracking-wide shrink-0">
                    Edited
                  </span>
                ) : null}
              </div>
              <textarea
                value={entry.value}
                onChange={(e) => {
                  handleValueChange(entry.key, e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                ref={(el) => {
                  if (el) {
                    el.style.height = 'auto';
                    el.style.height = el.scrollHeight + 'px';
                  }
                }}
                className="flex-1 px-2 py-1 text-[8px] xl:text-[10px] 3xl:text-xs bg-transparent border-0 rounded focus:outline-none focus:ring-1 focus:ring-blue-300 focus:bg-white resize-none font-mono leading-relaxed overflow-hidden text-slate-800"
                rows={1}
                style={{ minHeight: '24px' }}
              />
            </div>
          ))}

        </div>

        {/* Add Attribute — below entries */}
        <div className="px-4 py-2 border-t border-slate-100 flex justify-end">
          {isAddingEntry ? (
            <div className="flex items-center gap-2.5 w-full">
              <div className="w-0.5 self-stretch rounded-full shrink-0 bg-blue-400" />
              <input
                type="text"
                value={newEntryKey}
                onChange={(e) => setNewEntryKey(e.target.value)}
                placeholder="Attribute name..."
                className="w-[26%] shrink-0 px-2 py-1 text-[10px] xl:text-xs 3xl:text-sm border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 font-mono bg-white"
                autoFocus
              />
              <textarea
                value={newEntryValue}
                onChange={(e) => {
                  setNewEntryValue(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                placeholder="Value..."
                className="flex-1 px-2 py-1 text-[10px] xl:text-xs 3xl:text-sm border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 resize-none font-mono leading-relaxed overflow-hidden bg-white"
                rows={1}
                style={{ minHeight: '24px' }}
              />
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={handleCancelNewEntry}
                  className="px-2 py-0.5 text-[10px] xl:text-xs 3xl:text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveNewEntry}
                  className="px-2 py-0.5 text-[10px] xl:text-xs 3xl:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={handleAddEntry}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] xl:text-xs 3xl:text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md shadow-sm transition-all duration-200"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add attribute
            </button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Construction Review"
        message="Are you sure you want to delete this Construction Review? This action cannot be undone and all data will be permanently removed."
        itemType="CR"
        confirmButtonText="Delete"
      />
    </div>
  );
};
